import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { LifeBuoy, Loader2, Search, MessagesSquare, Trash2, Plus, Send } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  open: { label: "Open", cls: "bg-blue-100 text-blue-700 hover:bg-blue-100" },
  in_progress: { label: "In Progress", cls: "bg-amber-100 text-amber-700 hover:bg-amber-100" },
  waiting_customer: { label: "Waiting on Customer", cls: "bg-purple-100 text-purple-700 hover:bg-purple-100" },
  resolved: { label: "Resolved", cls: "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" },
};
const STATUSES = ["open", "in_progress", "waiting_customer", "resolved"];
const fmt = (d) => (d ? new Date(d).toLocaleString() : "");

export default function SupportManager() {
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState("in_progress");
  const [busy, setBusy] = useState(false);
  const [canned, setCanned] = useState([]);
  const [cannedOpen, setCannedOpen] = useState(false);
  const [newCanned, setNewCanned] = useState({ title: "", body: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/support/tickets", { params: { status: filter, q } });
      setTickets(data.tickets); setCounts(data.counts);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filter, q]);

  const loadCanned = useCallback(async () => {
    try { const { data } = await api.get("/admin/support/canned"); setCanned(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCanned(); }, [loadCanned]);

  const openTicket = async (t) => {
    setActive(t); setThread(null); setStatus(t.status); setReply("");
    try { const { data } = await api.get(`/admin/support/tickets/${t.id}`); setThread(data); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${active.id}/reply`, { message: reply.trim(), status });
      setReply("");
      const { data } = await api.get(`/admin/support/tickets/${active.id}`); setThread(data);
      toast.success("Reply sent — the student was emailed"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changeStatus = async (st) => {
    setStatus(st);
    try { await api.put(`/admin/support/tickets/${active.id}/status`, { status: st }); load(); toast.success("Status updated"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addCanned = async () => {
    try { await api.post("/admin/support/canned", newCanned); setNewCanned({ title: "", body: "" }); loadCanned(); toast.success("Saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delCanned = async (id) => { await api.delete(`/admin/support/canned/${id}`); loadCanned(); };

  return (
    <div data-testid="admin-support-manager">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-[#0A192F] flex items-center gap-2"><LifeBuoy className="h-5 w-5 text-amber-600" /> Support Desk</h3>
        <Button size="sm" variant="outline" onClick={() => setCannedOpen(true)} data-testid="admin-canned-btn"><MessagesSquare className="h-4 w-4 mr-1.5" /> Canned Responses</Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1" data-testid="admin-support-filters">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === s ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300"}`} data-testid={`admin-support-filter-${s}`}>
              {s === "all" ? "All" : STATUS_META[s].label} <span className="opacity-60">({counts[s] ?? 0})</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject or email…" className="pl-9 h-9" data-testid="admin-support-search" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="admin-support-ticket-list">
        {loading ? <div className="p-6 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          : tickets.length === 0 ? <div className="p-6 text-slate-400 text-sm">No tickets match.</div>
          : tickets.map((t) => (
            <button key={t.id} onClick={() => openTicket(t)} className="w-full text-left p-4 flex items-center justify-between gap-3 hover:bg-amber-50/50" data-testid={`admin-support-ticket-${t.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-[#0A192F] truncate flex items-center gap-2">{t.subject}{t.unread_by_admin && <span className="h-2 w-2 rounded-full bg-red-500" title="Unread" />}</div>
                <div className="text-xs text-slate-400">{t.name} · {t.email} · {fmt(t.last_reply_at)}</div>
              </div>
              <Badge className={STATUS_META[t.status]?.cls}>{STATUS_META[t.status]?.label || t.status}</Badge>
            </button>
          ))}
      </div>

      {/* Ticket thread */}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setThread(null); } }}>
        <DialogContent className="max-w-lg" data-testid="admin-support-thread-dialog">
          <DialogHeader><DialogTitle className="pr-6 truncate">{active?.subject}</DialogTitle></DialogHeader>
          {!thread ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
            <>
              <div className="text-xs text-slate-500 -mt-2">{thread.ticket.name} · {thread.ticket.email}</div>
              <div className="space-y-3 max-h-[42vh] overflow-y-auto pr-1 mt-2" data-testid="admin-thread-messages">
                {thread.messages.map((m) => (
                  <div key={m.id} className={`rounded-lg p-3 text-sm ${m.author === "admin" ? "bg-amber-50 border border-amber-100" : "bg-slate-50 border border-slate-100"}`}>
                    <div className="text-xs text-slate-400 mb-1">{m.author === "admin" ? (m.author_name || "Support Team") : m.author_name || "Customer"} · {fmt(m.created_at)}</div>
                    <div className="text-slate-700 whitespace-pre-wrap">{m.body}</div>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500 shrink-0">Status</Label>
                  <Select value={status} onValueChange={changeStatus}>
                    <SelectTrigger className="h-8 text-xs" data-testid="admin-ticket-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
                  </Select>
                  {canned.length > 0 && (
                    <Select onValueChange={(v) => { const c = canned.find((x) => x.id === v); if (c) setReply((r) => (r ? r + "\n\n" : "") + c.body); }}>
                      <SelectTrigger className="h-8 text-xs" data-testid="admin-canned-insert"><SelectValue placeholder="Insert canned…" /></SelectTrigger>
                      <SelectContent>{canned.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </div>
                <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" data-testid="admin-reply-input" />
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" disabled={busy || !reply.trim()} onClick={sendReply} data-testid="admin-reply-send"><Send className="h-4 w-4 mr-1.5" /> Send Reply &amp; Email</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Canned responses manager */}
      <Dialog open={cannedOpen} onOpenChange={setCannedOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-canned-dialog">
          <DialogHeader><DialogTitle>Canned Responses</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
            {canned.map((c) => (
              <div key={c.id} className="rounded-lg border border-slate-200 p-3">
                <div className="flex items-center justify-between"><span className="font-medium text-[#0A192F] text-sm">{c.title}</span><Button size="sm" variant="ghost" className="text-red-500 h-7" onClick={() => delCanned(c.id)}><Trash2 className="h-4 w-4" /></Button></div>
                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.body}</p>
              </div>
            ))}
          </div>
          <div className="border-t pt-3 space-y-2">
            <Input value={newCanned.title} onChange={(e) => setNewCanned({ ...newCanned, title: e.target.value })} placeholder="Title" data-testid="admin-canned-title" />
            <Textarea rows={3} value={newCanned.body} onChange={(e) => setNewCanned({ ...newCanned, body: e.target.value })} placeholder="Response text" data-testid="admin-canned-body" />
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={addCanned} data-testid="admin-canned-add"><Plus className="h-4 w-4 mr-1.5" /> Add Response</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
